import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import {
  WORMHOLE_OUTPUT_CARDS,
  WORMHOLE_OUTPUT_LENGTHS,
  DEFAULT_CREATIVITY_SLIDER_POSITION,
  REWRITE_OUTPUT_CARDS,
  DEFAULT_WRITE_MODEL,
  DEFAULT_COMMAND_MENU_MODE,
  PARAGRAPH_SPACING,
} from "../../../src/shared/constants.js";
import { applyEditorOptionDefaults } from "./userDataDefaults";

const collection = () => firebase.firestore().collection("userData");

const DEFAULTS = {
  font: "Quattro",
  fontSize: 20,
  lineHeight: 1.0,
  // Fallback for accounts whose doc predates the setting — keeps the legacy
  // tight layout. New accounts get MEDIUM written server-side at signup.
  paragraphSpacing: PARAGRAPH_SPACING.X1,
  textIndent: false,
  mainSize: 870,
  rightSize: 368,
  showWordCount: true,
  needsDemo: false,
  needsImport: false,
  summaryEnabled: false,
  expandEnabled: false,
  relatedWordsEnabled: false,
  needsFeedback: false,
  poemPaintEnabled: false,
  needsFilterHighlight: true,
  sentAutoMagicLink: false,
  newWormholeEnabled: false,
  optIntoNewWormhole: false, // whether we opt the user into wormhole 2 for them
  wormholeOutputLength: WORMHOLE_OUTPUT_LENGTHS[2].value,
  wormholeOutputCards: WORMHOLE_OUTPUT_CARDS.find((item) => item.value === 4).value,
  rewriteOutputCards: REWRITE_OUTPUT_CARDS.find((item) => item.value === 2).value,
  expandOptions: {
    customEnabled: false,
    summary: "",
    scene: "",
  },
  filterEnabled: false,
  documentsOrder: [],
  theme: "sorbet",
  mode: "light",
  creativitySliderPosition: DEFAULT_CREATIVITY_SLIDER_POSITION,
  hasInteractedWithCanvas: false,
  hasSeenWriteOnboarding: false,
  hasSeenRewriteOnboarding: false,
  hasSeenParagraphSpacingOnboarding: false,
  hideWhatsNewCards: false,
  hideStoryBibleOnboarding: false,
  hideCleanCopyShareMenuOnboarding: false,
  hideCleanCopyShareMenuTooltip: false,
  hasSeenSBModal: false,
  showCreditsUsedToast: false,
  writeModel: DEFAULT_WRITE_MODEL,
  showPluginTip: false,
  showPluginOnboarding: true,
  commandMenuMode: DEFAULT_COMMAND_MENU_MODE,
  genrePrimary: "",
  disabledPromoCards: [],
  hideUnlimitedExperimentWriteTooltip: false,
  hideUnlimitedExperimentDraftTooltip: false,
  hasReorderableMenu: false,
  feedbackToolbarMigrated: false,
  showMyVoiceWriteOnboarding: false,
  /** Light-mode hex for manuscript highlighter; agent highlights use this color (see ThemeAwareHighlight). */
  preferredManuscriptHighlightLight: "#FFF3BF",
  spellCheckEnabled: true,
  grammarCheckEnabled: true,
  languagePreference: "en-US",
};

export const FONTS = [
  { value: "Quattro", label: "Quattro" },
  { value: "Courier", label: "Courier" },
  { value: "Merriweather", label: "Merriweather" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Helvetica Neue", label: "Helvetica Neue" },
  { value: "OpenDyslexic", label: "OpenDyslexic" },
];

export const MODES = [
  { value: "lLght", label: "Light" },
  { value: "Dark", label: "Dark" },
];

export const THEMES = [
  { value: "sorbet", label: "Sorbet" },
  { value: "sorbet-auto", label: "Automatic Dark Mode" },
  { value: "amber", label: "Amber" },
  { value: "forest", label: "Forest" },
  { value: "aurora-borealis", label: "Aurora Borealis" },
  { value: "cauldron", label: "Cauldron" },
  { value: "charcoal", label: "Charcoal" },
  { value: "moonbeam", label: "Moonbeam" },
  { value: "sepia", label: "Sepia" },
];

export default class UserData {
  constructor(userId) {
    if (!userId) {
      throw "Must provide userId";
    }
    this.userId = userId;
    this.data = null;
    this.fetched = false;
  }

  async exists() {
    let snap = await collection().doc(this.userId).get();
    return snap.exists;
  }

  setLocalData(data) {
    this.data = data && typeof data === "object" ? data : {};

    // set defaults on keys that do not exist on server
    Object.keys(DEFAULTS).forEach((key) => {
      if (this.data[key] === undefined) {
        this.data[key] = DEFAULTS[key];
      }
    });

    // SW-9064: existing users may have null (not undefined) for the new
    // spell/grammar/language fields; treat null the same as missing.
    this.data = applyEditorOptionDefaults(this.data);
  }

  async fetchAndListen(callback) {
    return new Promise((resolve, reject) => {
      let initialHandled = false;
      const unsubscribe = collection().doc(this.userId).onSnapshot(
        (doc) => {
          if (!initialHandled) {
            initialHandled = true;
            if (!doc.exists) {
              unsubscribe();
              resolve(false);
              return;
            }
            this.setLocalData(doc.data());
            this.fetched = true;
            resolve(this);
          } else {
            this.setLocalData(doc.data());
          }

          if (callback) callback(this);

          if (!doc.data()?.usedPlatforms?.includes("web")) {
            try {
              collection().doc(this.userId).update({
                usedPlatforms: firebase.firestore.FieldValue.arrayUnion("web"),
              });
            } catch (e) {
              console.error("Error updating usedPlatforms", e);
            }
          }
        },
        (error) => {
          if (!initialHandled) {
            initialHandled = true;
            unsubscribe();
            reject(error);
          }
        },
      );
    });
  }

  async fetch() {
    let snap = await collection().doc(this.userId).get();

    if (!snap.exists) {
      return false;
    }

    this.setLocalData(snap.data());

    this.fetched = true;
    return this;
  }

  async update(data) {
    if (!data || typeof data !== "object") {
      return false;
    }

    // optimistically update the local data
    this.data = Object.assign(this.data || {}, data);

    await collection().doc(this.userId).update(data);
    return true;
  }

  async resetToDefaults() {
    await this.update(DEFAULTS);
    return true;
  }

  // translates from font db value suitable for font-family value
  static toFontFamily(font) {
    if (font.indexOf("Helvetica") !== -1) {
      // windows doesn't have helvetica, so we apply fallbacks
      return '"Helvetica Neue", Helvetica, Arial, Sans-Serif';
    } else {
      return font;
    }
  }

  static defaultValues() {
    return DEFAULTS;
  }
}
